import { V, V2d } from "../../core/Vector";
import { VectorSegment } from "./VectorSprite";

/**
 * Create line segments from polygon points
 * @param points Polygon vertices
 * @param color Color for all segments
 * @param thickness Thickness for all segments
 * @returns Array of line segments forming the polygon outline
 */
export function createPolygonSegments(
  points: V2d[],
  color: number,
  thickness: number = 1,
): VectorSegment[] {
  const segments: VectorSegment[] = [];

  for (let i = 0; i < points.length; i++) {
    const start = points[i];
    const end = points[(i + 1) % points.length];
    segments.push({ start, end, color, thickness });
  }

  return segments;
}

/**
 * Create line segments from character data (for vector text)
 * @param characterSegments Array of character segments with start/end arrays
 * @param color Color for all segments
 * @param thickness Thickness for all segments
 * @param scale Scale factor to apply to coordinates
 * @param offset Position offset to apply
 * @returns Array of line segments
 */
export function createCharacterSegments(
  characterSegments: Array<{ start: number[]; end: number[] }>,
  color: number,
  thickness: number = 1,
  scale: number = 1,
  offset: V2d = V(0, 0),
): VectorSegment[] {
  return characterSegments.map((segment) => ({
    start: V(
      segment.start[0] * scale + offset.x,
      segment.start[1] * scale + offset.y,
    ),
    end: V(
      segment.end[0] * scale + offset.x,
      segment.end[1] * scale + offset.y,
    ),
    color,
    thickness,
  }));
}

/**
 * Create line segments from start/end point pairs
 * @param pairs Array of [start, end] point pairs
 * @param color Color for all segments
 * @param thickness Thickness for all segments
 * @returns Array of line segments
 */
export function createSegmentsFromPairs(
  pairs: Array<[V2d, V2d]>,
  color: number,
  thickness: number = 1,
): VectorSegment[] {
  return pairs.map(([start, end]) => ({ start, end, color, thickness }));
}

/**
 * Create a cross pattern (+ shape)
 * @param size Half-size of the cross arms
 * @param color Color for all segments
 * @param thickness Thickness for all segments
 * @returns Array of line segments forming a cross
 */
export function createCrossSegments(
  size: number,
  color: number,
  thickness: number = 1,
): VectorSegment[] {
  return [
    { start: V(-size, 0), end: V(size, 0), color, thickness },
    { start: V(0, -size), end: V(0, size), color, thickness },
  ];
}

/**
 * Create an X pattern (× shape)
 * @param size Half-size of the X arms
 * @param color Color for all segments
 * @param thickness Thickness for all segments
 * @returns Array of line segments forming an X
 */
export function createXSegments(
  size: number,
  color: number,
  thickness: number = 1,
): VectorSegment[] {
  return [
    { start: V(-size, -size), end: V(size, size), color, thickness },
    { start: V(-size, size), end: V(size, -size), color, thickness },
  ];
}

/**
 * Create line segments from extracted polygon line segments
 * (useful for effects like BrokenExpandEffect)
 * @param extractedSegments Segments from PolygonUtils.extractLineSegments
 * @param colors Array of colors (will cycle through)
 * @param thickness Thickness for all segments
 * @returns Array of line segments
 */
export function createSegmentsFromExtracted(
  extractedSegments: Array<{ start: V2d; end: V2d }>,
  colors: number[],
  thickness: number = 1,
): VectorSegment[] {
  return extractedSegments.map((segment, i) => ({
    start: segment.start,
    end: segment.end,
    color: colors[i % colors.length],
    thickness,
  }));
}

/**
 * Duplicate segments with random color variation
 * @param baseSegments Base segments to duplicate
 * @param colors Array of colors to choose from
 * @returns Array of segments with randomized colors
 */
export function randomizeSegmentColors(
  baseSegments: VectorSegment[],
  colors: number[],
): VectorSegment[] {
  return baseSegments.map((segment) => ({
    ...segment,
    color: colors[Math.floor(Math.random() * colors.length)],
  }));
}

/**
 * Scale all segments from origin
 * @param segments Segments to scale
 * @param scale Scale factor
 * @returns New array of scaled segments
 */
export function scaleSegments(
  segments: VectorSegment[],
  scale: number,
): VectorSegment[] {
  return segments.map((segment) => ({
    ...segment,
    start: V(segment.start.x * scale, segment.start.y * scale),
    end: V(segment.end.x * scale, segment.end.y * scale),
  }));
}

/**
 * Translate all segments by offset
 * @param segments Segments to translate
 * @param offset Translation vector
 * @returns New array of translated segments
 */
export function translateSegments(
  segments: VectorSegment[],
  offset: V2d,
): VectorSegment[] {
  return segments.map((segment) => ({
    ...segment,
    start: V(segment.start.x + offset.x, segment.start.y + offset.y),
    end: V(segment.end.x + offset.x, segment.end.y + offset.y),
  }));
}
